import React, { useMemo, useState } from 'react';
import { accessLabel } from './adminPermissions';

// Read-only user directory. Deliberately has NO role controls — granting
// admin lives only in the Admin Access card, so a mis-click in a long list
// can never promote someone.

const ROLE_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'user', label: 'Users' },
  { id: 'admin', label: 'Admins' },
];

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
};

const subColor = (status) => {
  if (status === 'active') return 'text-green-700';
  if (status === 'past_due') return 'text-red-600';
  if (status === 'canceled' || status === 'cancelled') return 'text-gray-400';
  return 'text-gray-600';
};

export default function UsersDirectory({ Section, users, loading }) {
  const [q, setQ] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return users.filter((u) => {
      const role = u.role || 'user';
      // "Admins" covers both tiers — an elevated admin is still an admin.
      if (roleFilter === 'admin' && role !== 'admin' && role !== 'superadmin') return false;
      if (roleFilter === 'user' && role !== 'user') return false;
      if (!needle) return true;
      // Match name, email, plan or subscription status.
      return [u.display_name, u.email, u.plan_code, u.sub_status]
        .some((f) => (f || '').toLowerCase().includes(needle));
    });
  }, [users, q, roleFilter]);

  const input = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200';

  return (
    <Section
      title={`Users (${filtered.length}${filtered.length !== users.length ? ` of ${users.length}` : ''})`}
      right={(
        <div className="flex gap-1">
          {ROLE_FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setRoleFilter(f.id)}
              className={`px-2 py-1 rounded text-xs ${
                roleFilter === f.id ? 'bg-blue-600 text-white' : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}
    >
      <input
        className={`${input} mb-3`}
        placeholder="Search by name, email, plan or status…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />

      {loading ? (
        <p className="text-gray-400 text-sm">Loading users…</p>
      ) : filtered.length === 0 ? (
        <p className="text-gray-400 text-sm">
          {users.length === 0 ? 'No users yet.' : 'No users match that search.'}
        </p>
      ) : (
        <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white">
              <tr className="text-left text-gray-400 border-b border-gray-100">
                <th className="py-1 pr-3">Name</th>
                <th className="py-1 pr-3">Email</th>
                <th className="py-1 pr-3">Role</th>
                <th className="py-1 pr-3">Sub</th>
                <th className="py-1 pr-3">Plan</th>
                <th className="py-1">Joined</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.id} className="border-b border-gray-50">
                  <td className="py-1 pr-3 text-black">{u.display_name || '—'}</td>
                  <td className="py-1 pr-3 text-black">{u.email}</td>
                  <td className="py-1 pr-3">
                    {accessLabel(u.role, u.admin_permissions)
                      ? <span className="font-medium text-blue-700">{accessLabel(u.role, u.admin_permissions)}</span>
                      : <span className="text-gray-600">user</span>}
                  </td>
                  <td className={`py-1 pr-3 ${subColor(u.sub_status)}`}>{u.sub_status || '—'}</td>
                  <td className="py-1 pr-3 text-gray-600">{u.plan_code || '—'}</td>
                  <td className="py-1 text-gray-400">{fmtDate(u.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-gray-400 mt-2">
        View only — access is granted and changed in Admin Access below. Full access = every card; Standard admin = the default admin bundle; Custom = a hand-picked set of cards.
      </p>
    </Section>
  );
}
